import { InspectionOverviewDTO } from 'src/app/DTOs/InspectionOverviewDTO';import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { trigger, transition, style, animate } from '@angular/animations';

import { MasterDataService } from 'src/app/Services/master-data.service';
import { EmptyStateComponent } from 'src/app/Shared/Components/empty-state/empty-state.component';
import { AuthService } from 'src/app/Shared/Services/authentication.service';
import { tileRangeLabel } from 'src/app/helper/TileRangeLabel';
import { NotificationService } from 'src/app/Shared/prime-ng/notification.service';
import { DataResult, DataStateChangeEvent, GridDataResult, PageChangeEvent, State, pageSkip, pageTake, process } from 'src/app/Shared/prime-ng/grid-data';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';

@Component({
  selector: 'app-inspection-overview',
  standalone: true,
  imports: [PrimeNgModule, CommonModule,
    FormsModule,
    RouterModule,
    EmptyStateComponent
  ],
  templateUrl: './inspection-overview.component.html',
  styleUrl: './inspection-overview.component.css',
  animations: [
    trigger('cardAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class InspectionOverviewComponent implements OnInit, OnDestroy {
  public state: State = {
    skip: 0,
    take: 12,
    sort: [{ field: 'inspectionId', dir: 'desc' }]
  };

  public readonly statusQuickFilters = [
    { label: 'Alle', value: null },
    { label: 'Created', value: 1 },
    { label: 'Started', value: 2 },
    { label: 'Passed', value: 3 },
    { label: 'Failed', value: 4 }] as const;

  public gridView: GridDataResult = { data: [], total: 0 };
  public allRows: InspectionOverviewDTO[] = [];
  public loading = false;
  public searchText = '';
  public selectedStatusId: number | null = null;
  public activeActionMenuKey: string | null = null;
  private destroy$ = new Subject<void>();

  public get footerRangeLabel(): string {
    return tileRangeLabel(
      this.gridView.total,
      this.state.take ?? 12,
      this.state.skip ?? 0,
    );
  }

  constructor(
    private masterData: MasterDataService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.restoreStateFromQueryParams();
    this.loadInspections();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadInspections(): void {
    this.loading = true;
    this.masterData.getInspectionOverview()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.allRows = rows ?? [];
          this.applyFilters();
          this.loading = false;
        },
        error: () => {
          this.allRows = [];
          this.applyFilters();
          this.loading = false;
          this.notification('Error loading Inspections', 'error');
        }
      });
  }

  public onSearchChange(): void {
    this.state.skip = 0;
    this.applyFilters();
    this.syncQueryParams();
  }

  public clearSearch(): void {
    this.searchText = '';
    this.state.skip = 0;
    this.applyFilters();
    this.syncQueryParams();
  }

  public setStatusFilter(statusId: number | null): void {
    this.selectedStatusId = statusId;
    this.state.skip = 0;
    this.applyFilters();
    this.syncQueryParams();
  }

  public isStatusFilterActive(statusId: number | null): boolean {
    return this.selectedStatusId === statusId;
  }

  private applyFilters(): void {
    let filtered = [...this.allRows];

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      filtered = filtered.filter(item =>
        item.orderNumber?.toLowerCase().includes(search) ||
        item.inspectionType?.toLowerCase().includes(search) ||
        item.customer?.toLowerCase().includes(search) ||
        item.factory?.toLowerCase().includes(search) ||
        item.inspectors?.toLowerCase().includes(search)
      );
    }

    if (this.selectedStatusId !== null) {
      filtered = filtered.filter(item => item.inspectionStatusId === this.selectedStatusId);
    }

    this.gridView = process(filtered, this.state);
  }

  public pageChange(event: PageChangeEvent): void {
    this.state.skip = pageSkip(event);
    this.state.take = pageTake(event);
    this.applyFilters();
    this.syncQueryParams();
  }
  public toggleActionMenu(key: string | number, event: MouseEvent): void {    event.stopPropagation();    const menuKey = String(key);    this.activeActionMenuKey = this.activeActionMenuKey === menuKey ? null : menuKey;  }  public isActionMenuOpen(key: string | number): boolean {    return this.activeActionMenuKey === String(key);  }  public closeActionMenu(): void {    this.activeActionMenuKey = null;  }
  public createInspection(): void {
    this.router.navigate(['inspection-detail/0'], {
      queryParams: this.buildQueryParams(),
    });
  }

  public editInspection(inspection: InspectionOverviewDTO): void {
    this.router.navigate(['/inspection-detail', inspection.inspectionId], {
      queryParams: this.buildQueryParams(),
    });
  }
  public executeInspection(inspection: InspectionOverviewDTO): void {
    this.router.navigate(['/executeInspection', inspection.inspectionId]);
  }

  public openReport(inspection: InspectionOverviewDTO): void {
    if (!inspection.reportUrl) {
      this.notification('Für diese Inspektion ist noch kein Prüfbericht vorhanden.', 'warning');
      return;
    }

    window.open(inspection.reportUrl, '_blank', 'noopener');
  }

  public statusLabel(inspection: InspectionOverviewDTO): string {
    if (inspection.inspectionStatus) return inspection.inspectionStatus;
    switch (inspection.inspectionStatusId) {
      case 1: return 'Created';
      case 2: return 'Started';
      case 3: return 'Passed';
      case 4: return 'Failed';
      default: return '—';
    }
  }

  public statusClass(inspection: InspectionOverviewDTO): string {
    switch (inspection.inspectionStatusId) {
      case 1: return 'is-created';
      case 2: return 'is-started';
      case 3: return 'is-passed';
      case 4: return 'is-failed';
      default: return '';
    }
  }

  public isStarted(inspection: InspectionOverviewDTO): boolean {
    return inspection.inspectionStatusId > 1;
  }

  public hasReport(inspection: InspectionOverviewDTO): boolean {
    return !!inspection.reportUrl;
  }

  public get isAuditor(): boolean {
    return this.authService.isAuditor;
  }

  public get emptyStateSubline(): string {
    if (this.searchText || this.selectedStatusId !== null) {
      return 'Try adjusting your search criteria';
    }

    return this.isAuditor
      ? 'Für dich sind aktuell keine zugewiesenen Inspektionen vorhanden'
      : 'Click "New Inspection" to create your first inspection';
  }

  private restoreStateFromQueryParams(): void {
    const search = this.route.snapshot.queryParamMap.get('search');
    const status = Number(this.route.snapshot.queryParamMap.get('status'));
    const skip = Number(this.route.snapshot.queryParamMap.get('skip') ?? 0);
    const take = Number(this.route.snapshot.queryParamMap.get('take') ?? this.state.take ?? 12);

    this.searchText = search ?? '';
    this.selectedStatusId = Number.isInteger(status) && status > 0 ? status : null;
    this.state.skip = Number.isFinite(skip) && skip >= 0 ? skip : 0;
    this.state.take = Number.isFinite(take) && take > 0 ? take : 12;
  }

  private syncQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.buildQueryParams(),
      replaceUrl: true,
    });
  }

  private buildQueryParams(): Record<string, string | null> {
    return {
      search: this.searchText.trim() ? this.searchText.trim() : null,
      status: this.selectedStatusId !== null ? String(this.selectedStatusId) : null,
      skip: (this.state.skip ?? 0) > 0 ? String(this.state.skip) : null,
      take: (this.state.take ?? 12) !== 12 ? String(this.state.take) : null,
    };
  }

  private notification(message: string, state: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: state, icon: true }
    });
  }
}
